// ── Service de reset de senha (núcleo/aplicação) ──────────────────
// Validações de domínio antes de chegar na infra. Não conhece Supabase
// nem as RPCs — recebe o repositório pela porta.

import type {
  IPasswordResetRepository,
  PasswordResetLookup,
  AdminResetLink,
} from '../ports/IPasswordResetRepository';

/** Mesmo mínimo aplicado em `consume_password_reset`, que é quem de fato decide. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * A senha padrão do onboarding (`src/config/userDefaults.ts`) está no
 * bundle e é pública na prática. Redefinir "para" ela devolveria a conta
 * ao estado que o reset veio consertar.
 */
export const isWeakPassword = (password: string, defaultPassword?: string): boolean => {
  const value = String(password || '');
  if (value.length < MIN_PASSWORD_LENGTH) return true;
  if (defaultPassword && value === defaultPassword) return true;
  return false;
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export class PasswordResetService {
  constructor(private readonly repo: IPasswordResetRepository) {}

  requestReset = async (email: string, origin: string): Promise<void> => {
    const value = String(email || '').trim();
    if (!EMAIL_RE.test(value)) throw new Error('Informe um e-mail válido.');
    if (!origin) throw new Error('Origem indisponível.');
    await this.repo.requestReset(value, origin);
  };

  adminCreateReset = (userId: string, origin: string): Promise<AdminResetLink> => {
    if (!userId) throw new Error('Usuário obrigatório.');
    if (!origin) throw new Error('Origem indisponível.');
    return this.repo.adminCreateReset(userId, origin);
  };

  lookupReset = (token: string): Promise<PasswordResetLookup> => {
    if (!token) return Promise.resolve({ valid: false, status: 'invalid' });
    return this.repo.lookupReset(token);
  };

  consumeReset = async (
    token: string,
    password: string,
    confirmation: string,
    defaultPassword?: string,
  ): Promise<void> => {
    if (!token) throw new Error('Link inválido.');
    if (password !== confirmation) throw new Error('As senhas não conferem.');
    if (String(password || '').length < MIN_PASSWORD_LENGTH) {
      throw new Error(`A senha precisa ter ao menos ${MIN_PASSWORD_LENGTH} caracteres.`);
    }
    if (isWeakPassword(password, defaultPassword)) {
      throw new Error('Escolha uma senha diferente da senha padrão.');
    }
    await this.repo.consumeReset(token, password);
  };
}
